// ============================================================================
// UNIVERSAL ACTION HANDLERS - MAIN EXPORT
// ============================================================================

import type { UniversalActionHandlers } from "./types";
import type { BaseEntity, DetailActionContext } from "../types/detailPage";
import { standardCrudActions } from "./crud-actions";
import { communicationActions } from "./communication-actions";
import { fileManagementActions } from "./file-actions";
import { statusManagementActions } from "./status-actions";
import { integrationActions } from "./integration-actions";
import { enrichmentActions } from "./enrichment-actions";
import { entitySpecificActions } from "./entity-actions";
import { viewActions } from "./view-actions";
import { pdfFillActions } from "./pdf-actions";
import { linkingActions } from "./linking-actions";
import { aiAssistantActions } from "./ai-assistant-actions";
import { slackActions } from "./slack-actions";
import { automationActions } from "./automation-actions";
import { ticketCustomerEmailActions } from "./ticket-customer-email-actions";
import { auditActions } from "./audit-actions";

/**
 * Combined Universal Action Handlers
 * This is the main export that combines all action handlers
 */
export const universalActionHandlers: UniversalActionHandlers = {
  ...aiAssistantActions,
  ...standardCrudActions,
  ...communicationActions,
  ...fileManagementActions,
  ...statusManagementActions,
  ...integrationActions,
  ...enrichmentActions,
  ...entitySpecificActions,
  ...viewActions,
  ...pdfFillActions,
  ...linkingActions,
  ...slackActions,
  ...automationActions,
  ...ticketCustomerEmailActions,
  ...auditActions,
};

/**
 * Get action handler by action type
 */
export const getActionHandler = (actionType: string): UniversalActionHandlers[string] | undefined => {
  return universalActionHandlers[actionType];
};

/**
 * Check if an action type is supported
 */
export const isActionSupported = (actionType: string): boolean => {
  return actionType in universalActionHandlers;
};

/**
 * Get all supported action types
 */
export const getSupportedActionTypes = (): string[] => {
  return Object.keys(universalActionHandlers);
};

/**
 * Create a custom action handler for specific entity types
 */
export const createEntitySpecificHandler = (
  actionType: string,
  handler: (item: BaseEntity, context: DetailActionContext<BaseEntity>) => Promise<void> | void
): UniversalActionHandlers => {
  return {
    [actionType]: handler,
  };
};

/**
 * Extend universal handlers with custom handlers
 */
export const extendUniversalHandlers = (customHandlers: UniversalActionHandlers): UniversalActionHandlers => {
  return {
    ...universalActionHandlers,
    ...customHandlers,
  };
};

// Re-export type for backward compatibility
export type { UniversalActionHandlers };
